import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Link,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2canvas from 'html2canvas';
import { useSelector } from 'react-redux';
import { QRCode } from 'react-qrcode-logo';
import peacemakerImg from '../../../assets/images/homeimages/peacemakerImg.png';
import { Configuration } from '../../../assets/data/config';
import { jsPDF } from 'jspdf';
import imgUrl from '../../../assets/images/imgUrl';
import {apiServices} from '../../../services/apiServices';

const VisitingCard = () => {
  const { user } = useSelector((state) => state.auth);
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [certificateImage, setCertificateImage] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  
  // New state for visiting card from API
  const [visitingCardImage, setVisitingCardImage] = useState(null);
  const [visitingCardBlob, setVisitingCardBlob] = useState(null); // Store the original blob data
  const [visitingCardLoading, setVisitingCardLoading] = useState(false);
  const [visitingCardError, setVisitingCardError] = useState(null);
  const [isVisitingCardAutoGenerating, setIsVisitingCardAutoGenerating] = useState(false);
  const [refetchAttempts, setRefetchAttempts] = useState(0);
  
  // State for second page visiting card (QR) from API
  const [visitingCardQrImage, setVisitingCardQrImage] = useState(null);
  const [visitingCardQrBlob, setVisitingCardQrBlob] = useState(null); // Store the original blob data
  const [visitingCardQrLoading, setVisitingCardQrLoading] = useState(false);
  const [visitingCardQrError, setVisitingCardQrError] = useState(null);
  
  const visitingCardRef = useRef(null);
  const fetchingVisitingCardRef = useRef(false); // Track if fetch is in progress
  const fetchedUserIdRef = useRef(null); // Track which user ID we've fetched for

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to check if a blob URL is still valid
  const isValidBlobUrl = (url) => {
    if (!url || !url.startsWith('blob:')) return false;
    try {
      // Try to create a new URL object to validate the blob URL
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // User data for API calls
  const userData = {
    name: user?.username || user?.name || 'User Name',
    country: user?.country || 'Country',
    idNumber: user?.peacekeeper_id || 'IN-0000000-PK',
    mobile: user?.phone || user?.mobile || 'Mobile Number',
    email: user?.email || 'email@example.com',
    photo: user?.profile_picture || user?.avatar || peacemakerImg
  };


  // Fetch visiting card from API
  const fetchVisitingCard = async (forceRefetch = false) => {
    if (user?.userid) {
      // Don't refetch if we already fetched for this user ID (unless forced)
      if (!forceRefetch && fetchedUserIdRef.current === user.userid && visitingCardImage && isValidBlobUrl(visitingCardImage)) {
        console.log('Visiting card already fetched for this user, skipping duplicate fetch');
        return;
      }
      
      // Don't refetch if we already have a valid visiting card image (unless forced)
      if (!forceRefetch && visitingCardImage && isValidBlobUrl(visitingCardImage)) {
        console.log('Visiting card image already exists and is valid, skipping fetch');
        return;
      }
      
      // Prevent concurrent fetches using ref
      if (fetchingVisitingCardRef.current && !forceRefetch) {
        console.log('Visiting card fetch already in progress, skipping duplicate call');
        return;
      }
      
      fetchingVisitingCardRef.current = true;
      setVisitingCardLoading(true);
      setVisitingCardError(null);
      try {
        console.log('Fetching visiting card for user:', user.userid);
        // Use getBlobAPI to get the visiting card image as blob
        const response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        
        if (response.data) {
          console.log('Visiting card API response received, data size:', response.data.size || response.data.byteLength);
          
          // Create blob URL from the response
          const blob = new Blob([response.data], { type: 'image/png' });
          console.log('Blob created, size:', blob.size, 'type:', blob.type);
          
          // Store the original blob data for downloads
          setVisitingCardBlob(blob);
          
          const imageUrl = URL.createObjectURL(blob);
          console.log('Blob URL created:', imageUrl);
          
          // Test the blob URL immediately
          const testImg = new Image();
          testImg.onload = () => {
            console.log('Blob URL test successful, setting visiting card image');
            setVisitingCardImage(imageUrl);
            setRefetchAttempts(0); // Reset refetch attempts on successful fetch
            setVisitingCardLoading(false);
            fetchingVisitingCardRef.current = false;
          };
          testImg.onerror = (error) => {
            console.error('Blob URL test failed:', error);
            setVisitingCardError('Failed to load visiting card image');
            setVisitingCardLoading(false);
            fetchingVisitingCardRef.current = false;
            // Clean up the invalid blob URL
            URL.revokeObjectURL(imageUrl);
          };
          testImg.src = imageUrl;
        } else {
          console.log('No data received from visiting card API');
          setVisitingCardError('No visiting card data received');
          setVisitingCardLoading(false);
          fetchingVisitingCardRef.current = false;
        }
      } catch (error) {
        console.error('Error fetching visiting card:', error);
        
        // Check if it's a 404 error (visiting card not found)
        if (error.response?.status === 404) {
          console.log('Visiting card not found (404), attempting to generate automatically...');
          console.log('Error details:', error.response);
          setDownloadStatus('Visiting card not found. Generating automatically...');
          
          // Automatically call the generate API
          try {
            setIsVisitingCardAutoGenerating(true);
            const updateResponse = await apiServices.putAPI(`update-visiting-card/${userData.idNumber}`, {
              forceRegenerate: true
            });
            
            console.log('Visiting card generation response:', updateResponse);
            
            if (updateResponse.status === 200) {
              setDownloadStatus('Visiting card generated successfully! Fetching...');
              
              // Wait a moment for the visiting card to be generated, then retry fetching
              setTimeout(async () => {
                try {
                  const retryResponse = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
                  
                  if (retryResponse.data) {
                    const blob = new Blob([retryResponse.data], { type: 'image/png' });
                    setVisitingCardBlob(blob); // Store the blob data
                    const imageUrl = URL.createObjectURL(blob);
                    setVisitingCardImage(imageUrl);
                    setDownloadStatus('Visiting card generated and loaded successfully!');
                    console.log('Visiting card fetched after generation:', imageUrl);
                  }
                } catch (retryError) {
                  console.error('Error fetching visiting card after generation:', retryError);
                  setVisitingCardError('Visiting card was generated but failed to load. Please refresh the page.');
                } finally {
                  setIsVisitingCardAutoGenerating(false);
                  fetchingVisitingCardRef.current = false;
                }
              }, 2000); // Wait 2 seconds for generation to complete
              
            } else {
              throw new Error('Failed to generate visiting card');
            }
          } catch (updateError) {
            console.error('Error generating visiting card:', updateError);
            setVisitingCardError('Failed to generate visiting card. Please try again later.');
            setIsVisitingCardAutoGenerating(false);
            fetchingVisitingCardRef.current = false;
          }
        } else {
          // It's a different error, show the original error message
          setVisitingCardError('Failed to load visiting card. Please try again later.');
        }
      } finally {
        setVisitingCardLoading(false);
        fetchingVisitingCardRef.current = false;
      }
    }
  };

  // Fetch visiting card QR (second page) from API
  const fetchVisitingCardQr = async (forceRefetch = false) => {
    if (user?.userid) {
      // Don't refetch if we already have a valid visiting card QR image (unless forced)
      if (!forceRefetch && visitingCardQrImage && isValidBlobUrl(visitingCardQrImage)) {
        console.log('Visiting card QR image already exists and is valid, skipping fetch');
        return;
      }
      
      setVisitingCardQrLoading(true);
      setVisitingCardQrError(null);
      try {
        console.log('Fetching visiting card QR for user:', user.userid);
        // Use getBlobAPI to get the visiting card QR image as blob
        const response = await apiServices.getBlobAPI(`visiting-card-qr/${user.userid}`);
        
        if (response.data) {
          console.log('Visiting card QR API response received, data size:', response.data.size || response.data.byteLength);
          
          // Create blob URL from the response
          const blob = new Blob([response.data], { type: 'image/png' });
          console.log('QR Blob created, size:', blob.size, 'type:', blob.type);
          
          // Store the original blob data for downloads
          setVisitingCardQrBlob(blob);
          
          const imageUrl = URL.createObjectURL(blob);
          console.log('QR Blob URL created:', imageUrl);
          
          // Set the image URL immediately, don't wait for test
          setVisitingCardQrImage(imageUrl);
          setVisitingCardQrLoading(false);
          
          // Test the blob URL in the background
          const testImg = new Image();
          testImg.onload = () => {
            console.log('QR Blob URL test successful');
          };
          testImg.onerror = (error) => {
            console.error('QR Blob URL test failed:', error);
            // Clean up the invalid blob URL
            URL.revokeObjectURL(imageUrl);
            setVisitingCardQrImage(null);
            // Don't set error, keep loading state and retry
            setVisitingCardQrLoading(true);
            // Retry after a short delay
            setTimeout(() => {
              fetchVisitingCardQr(true);
            }, 1000);
          };
          testImg.src = imageUrl;
        } else {
          console.log('No data received from visiting card QR API');
          // Don't set error, keep loading and retry
          setVisitingCardQrLoading(false);
          setTimeout(() => {
            fetchVisitingCardQr(true);
          }, 2000);
        }
      } catch (error) {
        console.error('Error fetching visiting card QR:', error);
        
        // Don't set error state, keep loading and retry
        setVisitingCardQrLoading(false);
        
        // Retry after a delay (except for 404 which means it doesn't exist)
        if (error.response?.status !== 404) {
          setTimeout(() => {
            fetchVisitingCardQr(true);
          }, 2000);
        }
      }
    }
  };

  // Manual visiting card generation function
  const handleGenerateVisitingCard = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Generating new visiting card...');

      // Call the PUT endpoint to generate/update the visiting card
      const updateResponse = await apiServices.putAPI(`update-visiting-card/${userData.idNumber}`, {
        forceRegenerate: true
      });

      console.log('Visiting card generation response:', updateResponse);

      if (updateResponse.status === 200) {
        setDownloadStatus('Visiting card generated successfully! Fetching...');

        // Wait a moment for the visiting card to be generated, then fetch it
        setTimeout(async () => {
          try {
            const retryResponse = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);

            if (retryResponse.data) {
              const blob = new Blob([retryResponse.data], { type: 'image/png' });
              setVisitingCardBlob(blob); // Store the blob data
              const imageUrl = URL.createObjectURL(blob);
              setVisitingCardImage(imageUrl);
              setDownloadStatus('Visiting card generated and loaded successfully!');
              console.log('Visiting card fetched after generation:', imageUrl);
            }
          } catch (retryError) {
            console.error('Error fetching visiting card after generation:', retryError);
            setDownloadStatus('Visiting card was generated but failed to load. Please refresh the page.');
          }
        }, 2000); // Wait 2 seconds for generation to complete

      } else {
        throw new Error('Failed to generate visiting card');
      }
    } catch (error) {
      console.error('Error generating visiting card:', error);
      setDownloadStatus('Failed to generate visiting card. Please try again later.');
    } finally {
      setIsGenerating(false);
    }
  };


  // Fetch visiting card when component mounts
  useEffect(() => {
    // Only fetch if we have a user ID and haven't fetched for this user ID yet
    if (!user?.userid) {
      return;
    }
    
    // Skip if we've already fetched for this user ID
    if (fetchedUserIdRef.current === user.userid) {
      console.log('Skipping duplicate fetch for user:', user.userid);
      return;
    }
    
    console.log('Initializing fetch for user:', user.userid);
    
    // Mark that we're fetching for this user ID (set synchronously before async call)
    fetchedUserIdRef.current = user.userid;
    
    // Reset fetching refs to allow new fetch
    fetchingVisitingCardRef.current = false;
    
    // Fetch both cards
    fetchVisitingCard();
    fetchVisitingCardQr();
  }, [user?.userid]);


  // Fetch certificate image
  useEffect(() => {
    const fetchCertificate = async () => {
      if (user?.userid) {
        setCertificateLoading(true);
        setCertificateError(null);
        try {
          // Use getBlobAPI to get the certificate image as blob
          const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
          
          if (response.data) {
            // Create blob URL from the response
            const blob = new Blob([response.data], { type: 'image/png' });
            const imageUrl = URL.createObjectURL(blob);
            setCertificateImage(imageUrl);
            console.log('Certificate fetched successfully:', imageUrl);
          }
        } catch (error) {
          console.error('Error fetching certificate:', error);
          
          // Check if it's a 404 error (certificate not found)
          if (error.response?.status === 404) {
            console.log('Certificate not found (404), attempting to generate automatically...');
            console.log('Error details:', error.response);
            setShareStatus('Certificate not found. Generating automatically...');
            
            // Automatically call the generate API
            try {
              setIsAutoGenerating(true);
              const updateResponse = await apiServices.putAPI(`update-certificate/${userData.idNumber}`, {
                forceRegenerate: true
              });
              
              console.log('Certificate generation response:', updateResponse);
              
              if (updateResponse.status === 200) {
                setShareStatus('Certificate generated successfully! Fetching...');
                
                // Wait a moment for the certificate to be generated, then retry fetching
                setTimeout(async () => {
                  try {
                    const retryResponse = await apiServices.getBlobAPI(`certificate/${user.userid}`);
                    
                    if (retryResponse.data) {
                      const blob = new Blob([retryResponse.data], { type: 'image/png' });
                      const imageUrl = URL.createObjectURL(blob);
                      setCertificateImage(imageUrl);
                      setShareStatus('Certificate generated and loaded successfully!');
                      console.log('Certificate fetched after generation:', imageUrl);
                    }
                  } catch (retryError) {
                    console.error('Error fetching certificate after generation:', retryError);
                    setCertificateError('Certificate was generated but failed to load. Please refresh the page.');
                  } finally {
                    setIsAutoGenerating(false);
                  }
                }, 2000); // Wait 2 seconds for generation to complete
                
              } else {
                throw new Error('Failed to generate certificate');
              }
            } catch (updateError) {
              console.error('Error generating certificate:', updateError);
              setCertificateError('Failed to generate certificate. Please try again later.');
              setIsAutoGenerating(false);
            }
          } else {
            // It's a different error, show the original error message
            setCertificateError('Failed to load certificate. Please try again later.');
          }
        } finally {
          setCertificateLoading(false);
        }
      }
    };

    fetchCertificate();
  }, [user?.userid]);

  // Listen for certificate updates from EditProfile.js
  useEffect(() => {
    const handleCertificateUpdate = async (event) => {
      console.log('Certificate update event received in VisitingCard:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing certificate image in VisitingCard after profile update...');
        setDownloadStatus('Certificate updated! Refreshing with new profile information...');
        
        // Wait a moment for the backend to process, then refresh
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
            
            if (response.data) {
              // Clean up old blob URL to prevent memory leaks
              if (certificateImage && certificateImage.startsWith('blob:')) {
                URL.revokeObjectURL(certificateImage);
              }
              
              // Create new blob URL from the response
              const blob = new Blob([response.data], { type: 'image/png' });
              const imageUrl = URL.createObjectURL(blob);
              setCertificateImage(imageUrl);
              setDownloadStatus('Certificate refreshed successfully with updated profile information!');
              console.log('Certificate refreshed in VisitingCard after profile update:', imageUrl);
              
              // Clear success message after 3 seconds
              setTimeout(() => {
                setDownloadStatus('');
              }, 3000);
            }
          } catch (error) {
            console.error('Error refreshing certificate in VisitingCard after profile update:', error);
            setDownloadStatus('Certificate refresh failed. Please refresh the page manually.');
          }
        }, 2000); // Wait 2 seconds for backend processing
      }
    };

    // Add event listener
    window.addEventListener('certificateUpdated', handleCertificateUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('certificateUpdated', handleCertificateUpdate);
    };
  }, [user?.userid, certificateImage]);

  // Listen for visiting card updates from EditProfile.js
  useEffect(() => {
    const handleVisitingCardUpdate = async (event) => {
      console.log('Visiting card update event received in VisitingCard:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing visiting card image in VisitingCard after profile update...');
        setDownloadStatus('Visiting card updated! Refreshing with new profile information...');
        
        // Wait a moment for the backend to process, then refresh
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
            
            if (response.data) {
              // Clean up old blob URL to prevent memory leaks
              if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
                URL.revokeObjectURL(visitingCardImage);
              }
              
              // Create new blob URL from the response
              const blob = new Blob([response.data], { type: 'image/png' });
              setVisitingCardBlob(blob); // Store the blob data
              const imageUrl = URL.createObjectURL(blob);
              setVisitingCardImage(imageUrl);
              setDownloadStatus('Visiting card refreshed successfully with updated profile information!');
              console.log('Visiting card refreshed in VisitingCard after profile update:', imageUrl);
              
              // Clear success message after 3 seconds
              setTimeout(() => {
                setDownloadStatus('');
              }, 3000);
            }
          } catch (error) {
            console.error('Error refreshing visiting card in VisitingCard after profile update:', error);
            setDownloadStatus('Visiting card refresh failed. Please refresh the page manually.');
          }
        }, 2000); // Wait 2 seconds for backend processing
      }
    };

    // Add event listener
    window.addEventListener('visitingCardUpdated', handleVisitingCardUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('visitingCardUpdated', handleVisitingCardUpdate);
    };
  }, [user?.userid]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
        URL.revokeObjectURL(visitingCardImage);
      }
      if (visitingCardQrImage && visitingCardQrImage.startsWith('blob:')) {
        URL.revokeObjectURL(visitingCardQrImage);
      }
    };
  }, [visitingCardImage, visitingCardQrImage]);

  // Generate QR code data - registration URL based on usertype and temp_peacekeeper_id
  const generateQRCodeData = () => {
    const domain = Configuration.frontendDomain;
    const peacekeeperId = user?.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname
    if (user?.usertype === 'Entity' && user?.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    if (user?.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users
    return `${domain}/register?${peacekeeperId}`;
  };

  const qrCodeData = generateQRCodeData();

  const buildFilename = () => {
    // Extract name parts for proper formatting
    const safeName = (userData.name || 'User')
      .toString()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    
    // Get peacekeeper ID and clean it
    const peacekeeperId = (userData.idNumber || 'IN-00000000-PK').toString().trim();
    
    // Format: VC-FirstName-LastName-PeacekeeperID-IAPM
    const filename = `VC-${safeName}-${peacekeeperId}`;
    
    return filename;
  };

  // Helper function to check if download attribute is supported
  const isDownloadSupported = () => {
    const link = document.createElement('a');
    return 'download' in link;
  };

  // Helper function to detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to detect Safari browser
  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // iOS-specific download handler
  const handleIOSDownload = (blob, filename) => {
    try {
      console.log('Handling iOS download for:', filename);
      setDownloadStatus('Preparing download for iOS...');

      // Determine file type for proper MIME type
      const isPDF = filename.toLowerCase().endsWith('.pdf');
      const mimeType = isPDF ? 'application/pdf' : 'image/png';

      // For iOS, we need to use a different approach
      if (navigator.share && navigator.canShare) {
        // Try Web Share API first (iOS 13+)
        try {
          const file = new File([blob], filename, { type: mimeType });
          
          if (navigator.canShare({ files: [file] })) {
            const shareTitle = isPDF ? 'I Am Peacekeeper Visiting Card PDF' : 'I Am Peacekeeper Visiting Card';
            const shareText = isPDF ? `Download your visiting card PDF: ${filename}` : `Download your visiting card: ${filename}`;
            
            navigator.share({
              files: [file],
              title: shareTitle,
              text: shareText
            }).then(() => {
              setDownloadStatus('File shared successfully! Check your chosen app.');
            }).catch((error) => {
              console.log('Web Share failed, trying alternative method:', error);
              handleIOSFallbackDownload(blob, filename, isPDF);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, using fallback:', shareError);
          handleIOSFallbackDownload(blob, filename, isPDF);
          return;
        }
      }

      // Fallback for older iOS versions or if Web Share fails
      handleIOSFallbackDownload(blob, filename, isPDF);

    } catch (error) {
      console.error('iOS download failed:', error);
      handleIOSFallbackDownload(blob, filename, filename.toLowerCase().endsWith('.pdf'));
    }
  };

  // iOS-specific PDF download handler - Force save to device
  const handleIOSPDFDownload = (blob, filename) => {
    try {
      console.log('Handling iOS PDF download for:', filename);
      setDownloadStatus('Preparing PDF download for iOS...');

      // For iOS PDFs, we need to force save to device, not open in browser
      // Try multiple approaches in sequence
      
      // Approach 1: Try Web Share API first (most reliable for iOS)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            navigator.share({
              files: [file],
              title: 'I Am Peacekeeper Visiting Card PDF',
              text: `Save your visiting card PDF: ${filename}`
            }).then(() => {
              setDownloadStatus('PDF shared successfully! Choose "Save to Files" to save to your device.');
            }).catch((shareError) => {
              console.log('Web Share failed, trying alternative method:', shareError);
              handleIOSPDFFallback(blob, filename);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, trying fallback:', shareError);
          handleIOSPDFFallback(blob, filename);
          return;
        }
      }

      // If Web Share is not available, use fallback
      handleIOSPDFFallback(blob, filename);

    } catch (error) {
      console.error('iOS PDF download failed:', error);
      handleIOSPDFFallback(blob, filename);
    }
  };

  // iOS PDF fallback method - More aggressive approach
  const handleIOSPDFFallback = (blob, filename) => {
    try {
      console.log('Using iOS PDF fallback method for:', filename);
      setDownloadStatus('Preparing PDF for device save...');

      // Create a new blob with explicit PDF MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      // Try to use Cordova file system if available
      if (window.cordova && window.cordova.file) {
        console.log('Using Cordova file system for PDF save...');
        setDownloadStatus('Saving PDF to device using Cordova...');
        
        saveFileToDevice(pdfBlob, filename).then((filePath) => {
          console.log('PDF saved successfully to:', filePath);
          setDownloadStatus(`PDF saved to device: ${filename}`);
          
          // Show toast notification if available
          if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
            window.cordova.plugins.toast.show(`PDF saved: ${filename}`, 'long', 'bottom');
          }
        }).catch((error) => {
          console.error('Cordova save failed, trying web fallback:', error);
          handleIOSPDFWebFallback(pdfBlob, filename);
        });
        return;
      }

      // Web fallback for non-Cordova environments
      handleIOSPDFWebFallback(pdfBlob, filename);

    } catch (error) {
      console.error('iOS PDF fallback failed:', error);
      setDownloadStatus('PDF save failed. Please try the Web Share option.');
    }
  };

  // iOS PDF web fallback - Force download without opening
  const handleIOSPDFWebFallback = (pdfBlob, filename) => {
    try {
      console.log('Using iOS PDF web fallback for:', filename);
      setDownloadStatus('Preparing PDF download...');

      // Create a data URL instead of blob URL to force download
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element with aggressive download settings
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add attributes to force download
        link.setAttribute('download', filename);
        link.setAttribute('type', 'application/pdf');
        link.setAttribute('rel', 'noopener');
        
        // Add to DOM
        document.body.appendChild(link);
        
        // Try multiple click approaches
        try {
          // Method 1: Direct click
          link.click();
          
          // Method 2: Synthetic click event
          setTimeout(() => {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            link.dispatchEvent(clickEvent);
          }, 50);
          
          // Method 3: Programmatic click as last resort
          setTimeout(() => {
            if (link.click) {
              link.click();
            }
          }, 100);
          
          // Method 4: Try iframe approach for iOS (more aggressive)
          setTimeout(() => {
            try {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = dataUrl;
              document.body.appendChild(iframe);
              
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            } catch (iframeError) {
              console.log('Iframe method failed:', iframeError);
            }
          }, 200);
          
        } catch (clickError) {
          console.log('Click methods failed:', clickError);
        }
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 2000);

        setDownloadStatus('PDF download initiated. Check your Downloads or Files app.');
        
        // Show instructions for manual saving if needed
        setTimeout(() => {
          setDownloadStatus('If PDF opened in browser, long press the PDF and select "Save to Files" or "Add to Files".');
        }, 2000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading PDF blob:', error);
        setDownloadStatus('Failed to prepare PDF for download. Please try again.');
      };
      
      reader.readAsDataURL(pdfBlob);

    } catch (error) {
      console.error('iOS PDF web fallback failed:', error);
      setDownloadStatus('PDF download failed. Please try the Web Share option above.');
    }
  };

  // iOS fallback download method
  const handleIOSFallbackDownload = (blob, filename, isPDF = false) => {
    try {
      console.log('Using iOS fallback download method for:', filename, 'isPDF:', isPDF);
      setDownloadStatus('Opening file for iOS download...');

      // For PDFs on iOS, we need a different approach to prevent opening in the app
      if (isPDF) {
        // Create a blob URL instead of data URL for PDFs to avoid opening in browser
        const blobUrl = URL.createObjectURL(blob);
        
        // Create a temporary link element with proper attributes
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM and trigger download
        document.body.appendChild(link);
        
        // For iOS PDFs, we need to trigger the click in a user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        link.dispatchEvent(clickEvent);
        
        // Clean up blob URL after a delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
        }, 2000);

        setDownloadStatus('PDF download initiated. Check your Files app or Downloads.');
        
        // Show specific instructions for PDF downloads on iOS
        setTimeout(() => {
          setDownloadStatus('If PDF didn\'t download, try the "Download All Pages as PDF" button again or check your Files app.');
        }, 3000);

        return;
      }

      // For images, use the original data URL approach
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM and trigger download
        document.body.appendChild(link);
        
        // For iOS, we need to trigger the click in a user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        link.dispatchEvent(clickEvent);
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 1000);

        setDownloadStatus('Download initiated. Check your Downloads folder.');
        
        // Show instructions for iOS users
        setTimeout(() => {
          setDownloadStatus('If download didn\'t start, long press the image above and select "Save to Photos" or "Add to Files"');
        }, 2000);

        // Additional fallback: try opening in new tab for iOS
        setTimeout(() => {
          const newWindow = window.open(dataUrl, '_blank');
          if (newWindow) {
            setDownloadStatus('File opened in new tab. Long press and save on iOS.');
          }
        }, 3000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading blob for iOS:', error);
        setDownloadStatus('Failed to prepare file for download. Please try again.');
      };
      
      reader.readAsDataURL(blob);

    } catch (error) {
      console.error('iOS fallback download failed:', error);
      setDownloadStatus('Download failed. Please try saving the file manually.');
    }
  };

  // Create mobile download dialog with better user experience
  const createMobileDownloadDialog = (blob, filename) => {
    try {
      console.log('Creating mobile download dialog for:', filename);
      setDownloadStatus('Creating download dialog...');
      
      const url = URL.createObjectURL(blob);
      
      // Create a more prominent download dialog
      const downloadContainer = document.createElement('div');
      downloadContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.8);
        z-index: 10000;
        display: flex;
        justify-content: center;
        align-items: center;
      `;

      const dialogBox = document.createElement('div');
      dialogBox.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 15px;
        text-align: center;
        max-width: 90%;
        box-shadow: 0 10px 30px rgba(0,0,0,0.5);
      `;

      const title = document.createElement('h3');
      title.textContent = 'Download File';
      title.style.cssText = 'margin: 0 0 20px 0; color: #333; font-size: 20px;';

      const instructionText = document.createElement('p');
      instructionText.textContent = `Tap the button below to download: ${filename}`;
      instructionText.style.cssText = 'margin: 0 0 20px 0; color: #666; font-size: 16px;';

      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      downloadLink.textContent = `Download ${filename}`;
      downloadLink.style.cssText = `
        display: block;
        padding: 15px 30px;
        background: #007bff;
        color: white;
        text-decoration: none;
        border-radius: 8px;
        font-size: 18px;
        font-weight: bold;
        margin: 10px 0;
      `;

      const closeButton = document.createElement('button');
      closeButton.textContent = 'Close';
      closeButton.style.cssText = `
        padding: 10px 20px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        margin-top: 10px;
        font-size: 16px;
      `;

      closeButton.onclick = () => {
        document.body.removeChild(downloadContainer);
        URL.revokeObjectURL(url);
      };

      dialogBox.appendChild(title);
      dialogBox.appendChild(instructionText);
      dialogBox.appendChild(downloadLink);
      dialogBox.appendChild(closeButton);
      downloadContainer.appendChild(dialogBox);
      document.body.appendChild(downloadContainer);

      setDownloadStatus('Download dialog opened. Tap the download button above.');

      // Auto-remove after 60 seconds
      setTimeout(() => {
        if (document.body.contains(downloadContainer)) {
          document.body.removeChild(downloadContainer);
          URL.revokeObjectURL(url);
        }
      }, 60000);

    } catch (error) {
      console.error('Mobile download dialog failed:', error);
      fallbackDownload(blob, filename);
    }
  };

  // Alternative Android download method - similar to certificate download
  const tryAndroidDownload = (blob, filename) => {
    try {
      console.log('Trying alternative Android download method...');
      setDownloadStatus('Trying alternative Android download...');
      
      // Use the same approach as certificate download - create blob URL and download
      const url = URL.createObjectURL(blob);
      
      // Create a download link similar to certificate download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
      
      setDownloadStatus(`File downloaded: ${filename}`);
      
      // Show toast notification if available
      if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
        window.cordova.plugins.toast.show(`Downloaded: ${filename}`, 'long', 'bottom');
      }
      
    } catch (error) {
      console.error('Android download method failed:', error);
      setDownloadStatus('Android download failed, using fallback...');
      fallbackDownload(blob, filename);
    }
  };

  // Helper function to save file to a specific directory
  const saveFileToDirectory = (dirEntry, blob, filename, resolve, reject) => {
    dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
      fileEntry.createWriter((fileWriter) => {
        fileWriter.onwriteend = () => {
          const filePath = fileEntry.nativeURL || fileEntry.fullPath;
          console.log('File saved successfully to:', filePath);
          setDownloadStatus(`File saved to device: ${filename}`);
          resolve(filePath);
        };
        fileWriter.onerror = (error) => {
          console.error('Error writing file:', error);
          reject(error);
        };
        fileWriter.write(blob);
      }, (error) => {
        console.error('Error creating file writer:', error);
        reject(error);
      });
    }, (error) => {
      console.error('Error creating file:', error);
      reject(error);
    });
  };

  // Save file to device using Cordova
  const saveFileToDevice = async (blob, filename) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if we're in a Cordova environment
        if (window.cordova && window.cordova.file) {
          console.log('Using Cordova file API for device save');

          // Get the appropriate directory (prefer user-accessible locations)
          let directory;
          try {
            // Try to use user-accessible directories first
            if (window.cordova.file.externalRootDirectory) {
              // Try Downloads folder first
              directory = window.cordova.file.externalRootDirectory + 'Download/';
            } else if (window.cordova.file.documentsDirectory) {
              // Documents folder is usually accessible
              directory = window.cordova.file.documentsDirectory;
            } else if (window.cordova.file.externalDataDirectory) {
              // External data directory
              directory = window.cordova.file.externalDataDirectory;
            } else if (window.cordova.file.cacheDirectory) {
              // Cache directory as fallback
              directory = window.cordova.file.cacheDirectory;
            } else {
              // Last resort
              directory = window.cordova.file.dataDirectory;
            }
          } catch (dirError) {
            console.log('Error getting directory, using fallback:', dirError);
            directory = window.cordova.file.dataDirectory;
          }

          console.log('Using directory:', directory);

          // Use the modern Cordova file API
          if (window.resolveLocalFileSystemURL) {
            window.resolveLocalFileSystemURL(directory, (dirEntry) => {
              dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
                fileEntry.createWriter((fileWriter) => {
                  fileWriter.onwriteend = () => {
                    const filePath = fileEntry.nativeURL || fileEntry.fullPath;
                    console.log('File saved successfully to:', filePath);
                    
                    // Provide more helpful success message
                    let successMessage = `File saved: ${filename}`;
                    if (directory.includes('Download')) {
                      successMessage += '\nCheck your Downloads folder';
                    } else if (directory.includes('Documents')) {
                      successMessage += '\nCheck your Documents folder';
                    } else {
                      successMessage += '\nFile saved to app storage';
                    }
                    
                    setDownloadStatus(successMessage);
                    
                    // Show toast notification if available
                    if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
                      window.cordova.plugins.toast.show(`Downloaded: ${filename}`, 'long', 'bottom');
                    }
                    
                    resolve(filePath);
                  };

                  fileWriter.onerror = (error) => {
                    console.error('Error writing file:', error);
                    reject(error);
                  };

                  // Write the blob directly
                  fileWriter.write(blob);
                }, (error) => {
                  console.error('Error creating file writer:', error);
                  reject(error);
                });
              }, (error) => {
                console.error('Error creating file:', error);
                // Try to create the directory first if it doesn't exist
                if (error.code === 1) { // NOT_FOUND_ERR = 1
                  console.log('Directory not found, trying to create it...');
                  
                  // Try multiple directory creation strategies
                  const tryCreateDirectory = (baseDir, dirName, callback) => {
                    window.resolveLocalFileSystemURL(baseDir, (rootEntry) => {
                      rootEntry.getDirectory(dirName, { create: true }, (dirEntry) => {
                        callback(null, dirEntry);
                      }, (dirError) => {
                        console.log(`Failed to create ${dirName} in ${baseDir}, trying alternative...`);
                        callback(dirError, null);
                      });
                    }, (baseError) => {
                      console.log(`Failed to access ${baseDir}, trying alternative...`);
                      callback(baseError, null);
                    });
                  };
                  
                  // Try Downloads directory first
                  tryCreateDirectory(window.cordova.file.externalRootDirectory, 'Download', (err, dirEntry) => {
                    if (err) {
                      // Try Documents directory
                      tryCreateDirectory(window.cordova.file.documentsDirectory, '', (err2, dirEntry2) => {
                        if (err2) {
                          // Try external data directory
                          tryCreateDirectory(window.cordova.file.externalDataDirectory, '', (err3, dirEntry3) => {
                            if (err3) {
                              console.error('All directory creation attempts failed');
                              reject(err3);
                            } else {
                              saveFileToDirectory(dirEntry3, blob, filename, resolve, reject);
                            }
                          });
                        } else {
                          saveFileToDirectory(dirEntry2, blob, filename, resolve, reject);
                        }
                      });
                    } else {
                      saveFileToDirectory(dirEntry, blob, filename, resolve, reject);
                    }
                  });
                } else {
                  reject(error);
                }
              });
            }, (err) => {
              console.error('Error accessing directory:', err);
              reject(err);
            });
          } else {
            // Fallback to legacy method if modern API not available
            console.log('Using legacy Cordova file API');
            
            // Convert blob to base64 for legacy API
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const base64Data = reader.result.split(',')[1];
                
                window.cordova.file.writeFile(directory, filename, base64Data, true,
                  (success) => {
                    const filePath = directory + filename;
                    console.log('File saved successfully to:', filePath);
                    setDownloadStatus(`File saved to device: ${filename}`);
                    resolve(filePath);
                  },
                  (error) => {
                    console.error('Error writing file with legacy API:', error);
                    reject(error);
                  }
                );
              } catch (conversionError) {
                console.error('Error converting blob to base64:', conversionError);
                reject(conversionError);
              }
            };
            
            reader.onerror = (error) => {
              console.error('Error reading blob:', error);
              reject(error);
            };
            
            reader.readAsDataURL(blob);
          }
        } else {
          // Not in Cordova environment - use web download
          console.log('Not in Cordova environment, using web download');
          try {
            // Create a download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';

            // Add to DOM, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 1000);

            // Return a mock file path for consistency
            const mockPath = `file:///downloads/${filename}`;
            setDownloadStatus(`File downloaded: ${filename}`);
            resolve(mockPath);
          } catch (fallbackError) {
            console.error('Web download fallback failed:', fallbackError);
            reject(fallbackError);
          }
        }
      } catch (error) {
        console.error('Error in saveFileToDevice:', error);
        reject(error);
      }
    });
  };

  const fallbackDownload = (blob, filename) => {
    try {
      console.log('Using fallback download for:', filename);

      // Ensure the blob is valid
      if (!blob || blob.size === 0) {
        console.error('Invalid blob for download:', blob);
        setDownloadStatus('Download failed: Invalid file data');
        return;
      }

      // Check if we're on a mobile device
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        console.log('Mobile device detected, using mobile-friendly download...');
        setDownloadStatus('Opening file for mobile download...');

        // For mobile, try multiple approaches
        const url = URL.createObjectURL(blob);
        
        // First try: Direct download link
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
        
        setDownloadStatus(`Download initiated: ${filename}`);
        
        // If that doesn't work, try opening in new tab
        setTimeout(() => {
          const newWindow = window.open(url, '_blank');
          if (newWindow) {
            setDownloadStatus('File opened in new tab. Long press and save on mobile.');
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 5000);
          }
        }, 1000);
        
        return;
      }

      // Desktop download logic
      console.log('Desktop device, using standard download...');

      // Check if download attribute is supported
      if (isDownloadSupported()) {
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);

        // Trigger download
        link.click();

        // Clean up
        document.body.removeChild(link);

        // Clean up the URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        console.log('Fallback download completed for:', filename);
        setDownloadStatus(`Visiting card downloaded: ${filename}`);
      } else {
        // Download attribute not supported, open in new tab
        console.log('Download attribute not supported, opening in new tab...');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setDownloadStatus('Visiting card opened in new tab. Right-click and save as...');
      }
      
    } catch (error) {
      console.error('Error in fallbackDownload:', error);
      setDownloadStatus('Download failed. Please try again.');

      // Try one more time with a different approach
      try {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setDownloadStatus('Visiting card opened in new tab');
      } catch (finalError) {
        console.error('Final download attempt failed:', finalError);
        setDownloadStatus('Download completely failed. Please check your browser settings.');
      }
    }
  };

  const downloadBlob = (blob, filename) => {
    try {
      console.log('Starting download for:', filename, 'Blob size:', blob.size);

      // Enhanced mobile download handling with Cordova support
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOSDevice = isIOS();
      const isSafariBrowser = isSafari();
      
      console.log('Device type - Mobile:', isMobile, 'iOS:', isIOSDevice, 'Safari:', isSafariBrowser, 'Cordova available:', !!window.cordova);

      // Handle iOS devices specifically
      if (isIOSDevice) {
        console.log('iOS device detected, using iOS-specific download handler');
        
        // Use specific PDF handler for PDF files on iOS
        if (filename.toLowerCase().endsWith('.pdf')) {
          console.log('iOS PDF download detected, using specialized PDF handler');
          handleIOSPDFDownload(blob, filename);
        } else {
          handleIOSDownload(blob, filename);
        }
        return;
      }

      // Try Cordova file handling first if available
      if (window.cordova && window.cordova.file) {
        console.log('Attempting Cordova file save...');
        setDownloadStatus('Saving to device...');

        saveFileToDevice(blob, filename).then((filePath) => {
          console.log('File saved successfully to:', filePath);
          setDownloadStatus(`File saved to device: ${filename}`);
          
          // For Android, try to show a notification or toast
          if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
            window.cordova.plugins.toast.show(`File saved: ${filename}`, 'long', 'bottom');
          }
        }).catch((error) => {
          console.error('Cordova save failed, trying alternative methods:', error);
          
          // Try Web Share API first for mobile
          if (isMobile && navigator.share && navigator.canShare) {
            console.log('Trying Web Share API as fallback...');
            const fileType = filename.endsWith('.pdf') ? 'application/pdf' : 'image/png';
            const file = new File([blob], filename, { type: fileType });
            
            if (navigator.canShare({ files: [file] })) {
              navigator.share({
                files: [file],
                title: filename.endsWith('.pdf') ? 'Peacekeeper Visiting Card PDF' : 'Peacekeeper Visiting Card',
                text: `Download your ${filename.endsWith('.pdf') ? 'visiting card PDF' : 'visiting card image'}`
              }).then(() => {
                setDownloadStatus('File shared successfully! Check your chosen app.');
              }).catch((shareError) => {
                console.log('Web Share failed, trying download dialog:', shareError);
                createMobileDownloadDialog(blob, filename);
              });
              return;
            }
          }
          
          // Try alternative Android download method
          if (isMobile && /Android/i.test(navigator.userAgent)) {
            tryAndroidDownload(blob, filename);
          } else {
            setDownloadStatus('Device save failed, using download...');
            fallbackDownload(blob, filename);
          }
        });
        return;
      }

      // Try Web Share API for mobile devices (for images and PDFs)
      if (isMobile && navigator.share && navigator.canShare) {
        console.log('Attempting Web Share API...');
        const fileType = filename.endsWith('.pdf') ? 'application/pdf' : 'image/png';
        const file = new File([blob], filename, { type: fileType });
        
        if (navigator.canShare({ files: [file] })) {
          const shareTitle = filename.endsWith('.pdf') ? 'Peacekeeper Visiting Card PDF' : 'Peacekeeper Visiting Card';
          navigator.share({
            files: [file],
            title: shareTitle,
            text: `Download your ${filename.endsWith('.pdf') ? 'visiting card PDF' : 'visiting card image'}`
          }).then(() => {
            setDownloadStatus('File shared successfully!');
          }).catch((shareError) => {
            console.log('Web Share failed, falling back to download:', shareError);
            setDownloadStatus('Sharing failed, downloading instead...');
            fallbackDownload(blob, filename);
          });
          return;
        }
      }

      // For mobile devices, create a more user-friendly download experience
      if (isMobile) {
        console.log('Mobile device detected, creating mobile-friendly download...');
        console.log('Blob details - size:', blob.size, 'type:', blob.type, 'filename:', filename);
        setDownloadStatus('Preparing mobile download...');

        // Use the improved mobile download dialog
        createMobileDownloadDialog(blob, filename);
        return;
      }

      // Regular download for desktop or when other methods are not available
      console.log('Using fallback download method...');
      setDownloadStatus('Downloading file...');
      fallbackDownload(blob, filename);

    } catch (error) {
      console.error('Error:', error);
      setDownloadStatus('Download failed. Please try again.');
      // Ultimate fallback
      fallbackDownload(blob, filename);
    }
  };

  const handleDownloadPNG = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Capturing image from carousel...');

      // Determine filename based on current page
      let filename;
      if (currentPage === 0) {
        filename = buildFilename();
      } else {
        const baseFilename = buildFilename().replace('.png', '');
        filename = `${baseFilename}-Page${currentPage + 1}.png`;
      }
      
      // Ensure filename has .png extension
      if (!filename.toLowerCase().endsWith('.png')) {
        filename = filename + '.png';
      }

      // Capture the rendered carousel content to match exactly what's displayed
      const carouselElement = visitingCardRef.current;
      if (!carouselElement) {
        throw new Error('Carousel element not found');
      }

      // Find the image element within the carousel
      const imageElement = carouselElement.querySelector('img');
      if (!imageElement) {
        throw new Error('Image element not found in carousel');
      }

      // Wait for image to be fully loaded
      await new Promise((resolve) => {
        if (imageElement.complete) {
          resolve();
        } else {
          imageElement.onload = resolve;
          imageElement.onerror = resolve; // Continue even if error
        }
      });

      // Use html2canvas to capture the exact rendered content
      setDownloadStatus('Processing image...');
      const canvas = await html2canvas(imageElement, {
        backgroundColor: null, // Transparent background
        scale: 2, // Higher resolution
        useCORS: true,
        logging: false,
        width: imageElement.offsetWidth,
        height: imageElement.offsetHeight
      });

      // Convert canvas to blob
      await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            downloadBlob(blob, filename);
            setIsGenerating(false);
            setDownloadStatus('Download ready');
            resolve();
          } else {
            reject(new Error('Failed to create image blob'));
          }
        }, 'image/png', 1.0);
      });
        
    } catch (err) {
      console.error('Error downloading image:', err);
      setDownloadStatus('Failed to download image. Please try again.');
      setIsGenerating(false);
    }
  };

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  const handleDownload = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Generating high-quality visiting card PDF...');
      
      // Define all pages with their image sources (same as PNG download logic)
      const pages = [
        { 
          pageNumber: 0, 
          title: 'Visiting Card', 
          imageUrl: visitingCardImage,
          isDynamic: true 
        },
        { 
          pageNumber: 1, 
          title: 'QR Visiting Card', 
          imageUrl: visitingCardQrImage || imgUrl.visitingCard2,
          isDynamic: true 
        },
        { 
          pageNumber: 2, 
          title: 'Static Page 2', 
          imageUrl: imgUrl.visitingCard3,
          isDynamic: false 
        },
        { 
          pageNumber: 3, 
          title: 'Static Page 3', 
          imageUrl: imgUrl.visitingCard4,
          isDynamic: false 
        }
      ];
      
      // Ensure visiting card image is available
      if (!visitingCardImage || !visitingCardBlob) {
        setDownloadStatus('Loading visiting card image...');
        try {
          await fetchVisitingCard();
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!visitingCardImage || !visitingCardBlob) {
            throw new Error('Visiting card image is still not available');
          }
          // Update the pages array with the loaded image
          pages[0].imageUrl = visitingCardImage;
        } catch (error) {
          console.error('Error fetching visiting card:', error);
          throw new Error('Failed to load visiting card image. Please try again.');
        }
      }

      // Ensure QR visiting card image is available for page 1
      if (!visitingCardQrImage || !visitingCardQrBlob) {
        setDownloadStatus('Loading QR visiting card image...');
        try {
          await fetchVisitingCardQr();
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (visitingCardQrImage) {
            // Update the pages array with the loaded QR image
            pages[1].imageUrl = visitingCardQrImage;
          }
        } catch (error) {
          console.error('Error fetching QR visiting card:', error);
          // Continue with fallback image
        }
      } else {
        // Update the pages array with the existing QR image
        pages[1].imageUrl = visitingCardQrImage;
      }
      
      let pdf = null;
      let firstImageLoaded = false;
      let standardPdfWidth = 0;
      let standardPdfHeight = 0;
      let standardOrientation = "p";
      const originalPage = currentPage; // Store original page to restore later
      
      // Process each page by capturing from carousel (same as PNG download)
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        console.log(`Processing page ${i + 1}: ${page.title}`);
        setDownloadStatus(`Capturing page ${i + 1} of ${pages.length} from carousel...`);
        
        // For page 1 (QR visiting card), ensure it's loaded first
        if (i === 1 && (!visitingCardQrImage || visitingCardQrLoading)) {
          setDownloadStatus(`Loading QR visiting card for page ${i + 1}...`);
          try {
            await fetchVisitingCardQr(true);
            // Wait for image to be set
            let attempts = 0;
            while ((!visitingCardQrImage || visitingCardQrLoading) && attempts < 10) {
              await new Promise(resolve => setTimeout(resolve, 500));
              attempts++;
            }
          } catch (error) {
            console.error('Error loading QR visiting card:', error);
          }
        }
        
        // Switch to the page we want to capture
        setCurrentPage(i);
        // Wait for the page to render (longer wait for page 1)
        await new Promise(resolve => setTimeout(resolve, i === 1 ? 800 : 400));
        
        // Capture the rendered carousel content (same as PNG download)
        const carouselElement = visitingCardRef.current;
        if (!carouselElement) {
          console.error(`Carousel element not found for page ${i + 1}`);
          continue;
        }

        // Find the image element within the carousel - wait for it to appear
        let imageElement = carouselElement.querySelector('img');
        let attempts = 0;
        while (!imageElement && attempts < 10) {
          await new Promise(resolve => setTimeout(resolve, 200));
          imageElement = carouselElement.querySelector('img');
          attempts++;
        }
        
        if (!imageElement) {
          console.error(`Image element not found in carousel for page ${i + 1} after waiting`);
          continue;
        }

        // Wait for image to be fully loaded
        await new Promise((resolve) => {
          if (imageElement.complete && imageElement.naturalWidth > 0) {
            resolve();
          } else {
            const timeout = setTimeout(() => resolve(), 5000); // Max 5 second wait
            imageElement.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            imageElement.onerror = () => {
              clearTimeout(timeout);
              resolve(); // Continue even if error
            };
          }
        });

        // Use html2canvas to capture the exact rendered content (same as PNG)
        setDownloadStatus(`Processing page ${i + 1}...`);
        const canvas = await html2canvas(imageElement, {
          backgroundColor: null, // Transparent background
          scale: 2, // Higher resolution
          useCORS: true,
          logging: false,
          width: imageElement.offsetWidth,
          height: imageElement.offsetHeight
        });

        // Convert canvas to base64
        const highQualityBase64 = canvas.toDataURL('image/png', 1.0);
        
        // Get image dimensions from canvas
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        // Convert pixels to mm (assuming 96 DPI: 1 inch = 25.4mm, 96 pixels = 25.4mm)
        const pixelsToMm = 25.4 / 96;
        const pdfWidth = imgWidth * pixelsToMm;
        const pdfHeight = imgHeight * pixelsToMm;
        const orientation = imgWidth > imgHeight ? "l" : "p";
        
        // Use first page dimensions for all pages to ensure consistency
        if (!firstImageLoaded) {
          standardPdfWidth = pdfWidth;
          standardPdfHeight = pdfHeight;
          standardOrientation = orientation;
          console.log(`Using standard dimensions for all pages: ${standardPdfWidth}mm x ${standardPdfHeight}mm`);
          
          pdf = new jsPDF({
            orientation: standardOrientation,
            unit: "mm",
            format: [standardPdfWidth, standardPdfHeight],
            compress: false,
            hotfixes: ["px_scaling"]
          });
          firstImageLoaded = true;
        } else {
          // Add new page with same standard dimensions
          pdf.addPage([standardPdfWidth, standardPdfHeight], standardOrientation);
        }
        
        // Add the image to PDF at full size (fills entire page)
        pdf.addImage(highQualityBase64, "PNG", 0, 0, standardPdfWidth, standardPdfHeight, undefined, 'NONE');
      }
      
      // Restore original page
      setCurrentPage(originalPage);
      
      if (!pdf || !firstImageLoaded) {
        throw new Error('Failed to create PDF - no valid images found');
      }
      
      // Generate filename
      const baseFilename = buildFilename().replace('.png', '');
      const filename = `${baseFilename}.pdf`;
      
      // Generate PDF blob for download
      const pdfBlob = pdf.output("blob");
      
      setDownloadStatus('Downloading high-quality PDF...');
      
      // Use the improved downloadBlob function for consistent mobile handling
      downloadBlob(pdfBlob, filename);
      
      setDownloadStatus('High-quality PDF downloaded successfully!');
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (error) {
      console.error('Error downloading high-quality visiting card PDF:', error);
      setDownloadStatus('PDF download failed. Please try again.');
      setTimeout(() => setDownloadStatus(''), 3000);
    } finally {
      setIsGenerating(false);
    }
  };

  const nextPage = () => {
    const newPage = (currentPage + 1) % 4;
    setCurrentPage(newPage);
    
    // If going to page 0 (visiting card), check if the image is still valid
    if (newPage === 0 && visitingCardImage) {
      checkAndRefreshVisitingCard();
    }
  };

  const prevPage = () => {
    const newPage = (currentPage - 1 + 4) % 4;
    setCurrentPage(newPage);
    
    // If going to page 0 (visiting card), check if the image is still valid
    if (newPage === 0 && visitingCardImage) {
      checkAndRefreshVisitingCard();
    }
  };

  // Check if visiting card image is still valid and refresh if needed
  const checkAndRefreshVisitingCard = () => {
    if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
      // Test the blob URL to see if it's still valid
      const testImg = new Image();
      testImg.onload = () => {
        console.log('Visiting card blob URL is still valid');
      };
      testImg.onerror = () => {
        console.log('Visiting card blob URL is invalid, refreshing...');
        // Clear the invalid image and refetch
        if (visitingCardImage.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(visitingCardImage);
          } catch (e) {
            console.log('Error revoking blob URL:', e);
          }
        }
        setVisitingCardImage(null);
        fetchVisitingCard(true); // Force refetch
      };
      testImg.src = visitingCardImage;
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return renderDynamicPage();
      case 1:
        return renderStaticPage1();
        case 2:
        return renderStaticPage2();
        case 3:
        return renderStaticPage3();
      default:
        return renderDynamicPage();
    }
  };

  const renderDynamicPage = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      data-page="0"
      className="visiting-card-content"
    >
      {visitingCardLoading || isVisitingCardAutoGenerating ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={60} />
          <Typography sx={{ ml: 2 }}>
            {isVisitingCardAutoGenerating ? 'Generating visiting card automatically...' : 'Loading your visiting card...'}
          </Typography>
        </Box>
      ) : visitingCardError ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4, gap: 2 }}>
          <Typography color="error" variant="h6">{visitingCardError}</Typography>
          {!isVisitingCardAutoGenerating && (
            <Button
              variant="contained"
              onClick={handleGenerateVisitingCard}
              disabled={isGenerating}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#45a049' }
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Visiting Card'}
            </Button>
          )}
          {isVisitingCardAutoGenerating && (
            <Typography color="info" variant="body2">
              Visiting card generation is in progress. Please wait...
            </Typography>
          )}
        </Box>
      ) : visitingCardImage ? (
        <>
        <Box sx={{ textAlign: 'center' }} >
          <img
            src={visitingCardImage}
            alt="I Am Peacekeeper Visiting Card"
            style={{
              maxWidth: '325px',
              height: '532px'
            }}
            onLoad={() => {
              console.log('Visiting card image loaded successfully');
            }}
            onError={(e) => {
              // Clear the invalid image immediately
              if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(visitingCardImage);
                } catch (revokeError) {
                  console.log('Error revoking blob URL:', revokeError);
                }
              }
              setVisitingCardImage(null);
              
              // Limit refetch attempts to prevent infinite loops
              if (refetchAttempts < 3) {
                setRefetchAttempts(prev => prev + 1);
                // Refetch the visiting card after clearing the state
                setTimeout(() => {
                  fetchVisitingCard(true); // Force refetch
                }, 1000);
              } else {
                setRefetchAttempts(0); // Reset for future attempts
              }
            }}
          />
          
        </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          {isVisitingCardAutoGenerating ? (
            <>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2 }}>Generating visiting card automatically...</Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">No visiting card available</Typography>
              <Button
                variant="contained"
                onClick={handleGenerateVisitingCard}
                disabled={isGenerating}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#45a049' }
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Visiting Card'}
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  const renderStaticPage3 = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
      data-page="3"
      className="visiting-card-content"
    >
      <img 
        src={imgUrl.visitingCard4} 
        alt="Visiting Card Page 4"
        style={{
              maxWidth: '325px',
              height: '532px'
            }}
      />
    </Box>
  );

  const renderStaticPage2 = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
      data-page="2"
      className="visiting-card-content"
    >
      <img 
        src={imgUrl.visitingCard3} 
        alt="Visiting Card Page 3"
        style={{
              maxWidth: '325px',
              height: '532px'
            }}
      />
    </Box>
  );

  const renderStaticPage1 = () => {
    // Show loading state (including when there's an error but no image - keep trying)
    if (visitingCardQrLoading || !visitingCardQrImage) {
      return (
        <Box 
          sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '532px',
            gap: 2
          }}
          data-page="1"
          className="visiting-card-content"
        >
          <CircularProgress sx={{ color: '#3A8139' }} />
          <Typography variant="body1" color="text.secondary">
            Loading QR code...
          </Typography>
        </Box>
      );
    }

    // Show visiting card QR image from API
    if (visitingCardQrImage) {
      return (
        <Box 
          sx={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}
          data-page="1"
          className="visiting-card-content"
        >
          <img 
            src={visitingCardQrImage} 
            alt="Visiting Card Page 2 (QR)"
            style={{
              maxWidth: '325px',
              height: '532px'
            }}
            onLoad={() => {
              console.log('QR Visiting card image loaded successfully');
            }}
            onError={(e) => {
              // Clear the invalid image immediately
              if (visitingCardQrImage && visitingCardQrImage.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(visitingCardQrImage);
                } catch (revokeError) {
                  console.log('Error revoking QR blob URL:', revokeError);
                }
              }
              setVisitingCardQrImage(null);
              // Don't set error, keep loading state and retry
              setVisitingCardQrLoading(true);
              
              // Try to refetch
              setTimeout(() => {
                fetchVisitingCardQr(true);
              }, 1000);
            }}
          />
        </Box>
      );
    }

    // Fallback to static image if API image is not available
    return (
      <Box 
        sx={{ 
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center'
        }}
        data-page="1"
        className="visiting-card-content"
      >
        <img 
          src={imgUrl.visitingCard2} 
          alt="Visiting Card Page 2"
          style={{
            maxWidth: '325px',
            height: '532px'
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>

      {/* Page Indicator */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 3,
        gap: 1,
        alignItems: 'center'
      }}>
        {/* Previous Button - Left side */}
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={prevPage}
          disabled={isGenerating}
          size="small"
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.5
          }}
        >
          
        </Button>
        
        {/* Page Dots */}
        {[0, 1, 2, 3].map((page) => (
          <Box
            key={page}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: currentPage === page ? '#3A8139' : '#e0e0e0',
              cursor: 'pointer'
            }}
            onClick={() => {
              setCurrentPage(page);
              // If clicking on page 0 (visiting card), check if the image is still valid
              if (page === 0 && visitingCardImage) {
                checkAndRefreshVisitingCard();
              }
            }}
          />
        ))}
        
        {/* Next Button - Right side */}
        <Button
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          onClick={nextPage}
          disabled={isGenerating}
          size="small"
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.5
          }}
        >
          
        </Button>
      </Box>

      {/* Status Message */}
      {downloadStatus && (
        <Box sx={{ 
          textAlign: 'center', 
          mb: 2,
          p: 1,
          bgcolor: downloadStatus.includes('successfully') ? 'success.light' : 'info.light',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            {downloadStatus}
          </Typography>
        </Box>
      )}

      {/* Visiting Card Content */}
      <Box 
        className="visiting-card-content"
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%',
          minHeight: '600px'
        }}
      >
        <Box ref={visitingCardRef} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {currentPage === 0 ? (
            renderDynamicPage()
          ) : (
            renderPage()
          )}
        </Box>
      </Box>

      {/* Page Info */}
      <Box sx={{ 
        textAlign: 'center', 
        mt: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1
      }}>
        <Typography variant="body2" color="text.secondary">
          Page {currentPage + 1} of 4
          {currentPage === 0}
          {currentPage === 1} 
          {currentPage === 2}
          {currentPage === 3}
        </Typography>
      </Box>

       {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        
         <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPNG}
            disabled={isGenerating || visitingCardLoading || isVisitingCardAutoGenerating}
            sx={{
              borderColor: '#FF6B35',
              color: '#FF6B35',
              '&:hover': { 
                borderColor: '#E55A2B',
                backgroundColor: 'rgba(255, 107, 53, 0.04)'
              }
            }}
          >
            {isGenerating ? 'Generating...' : 'Download PNG'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownload}
            disabled={isGenerating || !visitingCardImage || visitingCardLoading || isVisitingCardAutoGenerating}
            sx={{
              bgcolor: '#3A8139',
              color: 'white',
              '&:hover': { bgcolor: '#2d652c' }
            }}
          >
            {isGenerating ? 'Generating...' : 'Download All Pages as PDF'}
          </Button>
          
        </Box>
      </Box>
    </Box>
  );
};

export default VisitingCard;